import React, {useEffect, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";
import dayjs from "dayjs";
import TimeLineStepper, {StepConfig} from "../../components/timeline/TimeLineStepper";
import SettingsIcon from "@mui/icons-material/Settings";
import SailingIcon from '@mui/icons-material/Sailing';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import DoneIcon from '@mui/icons-material/Done';
import RulesSettings from "./processing/RulesSettings";
import BoatRegistration from "./processing/BoatRegistration";
import FinishLineRecording from "./processing/FinishLineRecording";
import OverviewClosure from "./processing/OverviewClosure";
import {RaceProvider, useRace} from "../../utils/races/RaceContext";
import {CategoriesProvider, useCategories} from "../../utils/categories/CategoriesContext";
import PublishResultsDialog from "../../components/dialog/PublishResultsDialog";
import {deleteFetcher, getFetcher, postFetcher, putFetcher} from "../../utils/api/fetchers";
import {useNotification} from "../../utils/hooks/useNotification";
import {ResultsStatus} from "../../schemas/races/racesSchema";
import useQueryParams from "../../utils/hooks/useQueryParams";

const RaceProcessing = () => {
    const {raceId} = useParams<{ raceId: string }>();
    if (!raceId) return <ErrorMessage message="Chybí ID závodu"/>;
    return (
        <CategoriesProvider>
            <RaceProvider raceId={raceId}>
                <RaceProcessingInner/>
            </RaceProvider>
        </CategoriesProvider>
    );
};

const RaceProcessingInner: React.FC = () => {
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const {race, isLoading, error} = useRace();
    const {categories} = useCategories();
    const [openDialog, setOpenDialog] = useState(false);
    const {searchParams, setQueryParam} = useQueryParams();

    useEffect(() => {
        if (!race) return;
        const urlStep = searchParams.get("step");
        const savedStep = localStorage.getItem(`race_${race.id}_step`);
        if (!urlStep && savedStep) {
            setQueryParam("step", savedStep);
        }
        if (urlStep) {
            localStorage.setItem(`race_${race.id}_step`, urlStep);
        }
    }, [race, searchParams, setQueryParam]);

    const handleStepChange = (stepKey: string) => {
        if (race) {
            localStorage.setItem(`race_${race.id}_step`, stepKey);
        }
    };

    const steps: StepConfig[] = [
        {
            label: 'Nastavení pravidel',
            labelShort: 'Pravidla',
            key: 'rules',
            component: <RulesSettings/>,
            icon: <SettingsIcon/>,
            rightActions: [
                {
                    label: 'Přejít k registraci',
                    onClick: (goTo) => {
                        goTo(1);
                        handleStepChange('registration');
                    },
                },
            ],
        },
        {
            label: 'Registrace lodí', labelShort: 'Registrace',
            key: 'registration',
            component: <BoatRegistration/>,
            icon: <SailingIcon/>,
            leftActions: [
                {
                    label: 'Zpět k nastavení',
                    variant: 'outlined',
                    color: 'primary',
                    onClick: (goTo) => {
                        goTo(0);
                        handleStepChange('rules');
                    },
                },
            ],
            rightActions: [
                {
                    label: 'Přejít k zaznamenávání průjezdů závod',
                    color: 'primary',
                    onClick: (goTo) => {
                        goTo(2);
                        handleStepChange('finish');
                    },
                },
            ],
        },
        {
            label: 'Zaznamenávání průjezdů', labelShort: 'Průjezdy',
            key: 'finish',
            component: <FinishLineRecording/>,
            icon: <SportsScoreIcon/>,
            leftActions: [
                {
                    label: 'Zpět k registraci',
                    variant: 'outlined',
                    color: 'primary',
                    onClick: (goTo) => {
                        goTo(1);
                        handleStepChange('registration');
                    },
                },
            ],
            rightActions: [
                {
                    label: 'Přejít k shrnutí a ukončení závodu',
                    color: 'primary',
                    onClick: (goTo) => {
                        goTo(3);
                        handleStepChange('summary');
                    },
                },
            ],
        },
        {
            label: 'Přehled a uzavření',
            labelShort: 'Shrnutí',
            key: 'summary',
            component: <OverviewClosure/>,
            icon: <DoneIcon/>,
            leftActions: [
                {
                    label: 'Zpět k prlůjezdům',
                    variant: 'outlined',
                    color: 'primary',
                    onClick: (goTo) => {
                        goTo(2);
                        handleStepChange('finish');
                    },
                },
            ],
            rightActions: [
                {
                    label: 'Ukončit závod a publikovat výsledky',
                    color: 'error',
                    onClick: () => setOpenDialog(true),
                },
            ],
        },
    ];

    const handlePublishChoice = async (result: "create" | "create-publish") => {
        let continueProcessing: boolean;
        // generate race results, if already generated, delete them and generate again
        continueProcessing = await generateRaceResults();
        // set results status
        if (continueProcessing) {
            continueProcessing = await setResultsStatus(result === "create" ? "UNPUBLISHED" : "PUBLISHED");
        }
        // set race status as finished
        if (continueProcessing) {
            continueProcessing =  await finishRace();
        }
        // eventually recalculate seasons result based on new results publish status
        if (continueProcessing && result === "create-publish") {
            continueProcessing = await recalculateSeasonsResults();
        }

        if (continueProcessing) {
            showNotification("Závod byl úspěšně ukončen", "success");
            if (race) {
                localStorage.removeItem(`race_${race.id}_step`);
                navigate(`/results?season=${race.raceSeason.year}&type=race&race=${race.id}`);
            }
        } else {
            showNotification("Nepodařilo se ukončit závod", "error");
        }
    }

    const generateRaceResults = async (): Promise<boolean> => {
        if (!race || !categories) return false;
        const endpoints = [
            `/races/${race.id}/results/overall`,
            ...categories.map(cat => `/races/${race.id}/results/categories/${cat.id}`)
        ];

        // check if results already exist
        const results = await Promise.allSettled(
            endpoints.map(url =>
                getFetcher(url)
                    .then(() => true)
                    .catch(() => false)
            )
        );
        const anySuccess = results.some(r => r.status === 'fulfilled' && r.value === true);
        if (anySuccess) {
            try {
                await deleteFetcher(`/races/${race.id}/results`);
                showNotification("Výsledky byly smazány", "success");
            } catch (e) {
                showNotification("Nepodařilo se smazat výsledky závodu", "error");
                console.error(e);
                return false;
            }
        }

        // generate new results
        try {
            await postFetcher(`/races/${race.id}/results/generate`, {})
            showNotification("Výsledky závodu byly úspěšně vygenerovány", "success")
        } catch (e: any) {
            if (e.response?.status === 404) {
                showNotification("Nejsou evidovány průjezdy všech lodí v závodě", "error")
                return false;
            }
            showNotification("Nepodařilo se vygenerovat výsledky závodu", "error")
            console.error(e);
            return false;
        }
        return true;
    }

    const setResultsStatus = async (status: ResultsStatus): Promise<boolean> => {
        if (!race) return false;
        try {
            await putFetcher(`/races/${race.id}/status/results`, {
                resultStatus: status,
            });
            showNotification("Stav výsledků závodu byl úspěšně aktualizován", "success")
        } catch (e) {
            showNotification("Nepodařilo se aktualizovat stav výsledků závodu", "error")
            console.error(e);
            return false;
        }
        return true;
    }

    const recalculateSeasonsResults = async (): Promise<boolean> => {
        if (!race || !categories) return false;

        const endpoints = [
            `/seasons/${race.raceSeason.id}/results/overall`,
            ...categories.map(cat => `/seasons/${race.raceSeason.id}/results/categories/${cat.id}`)
        ];
        // Try check if season results exist
        const results = await Promise.allSettled(
            endpoints.map(url =>
                getFetcher(url)
                    .then(() => true)
                    .catch(() => false)
            )
        );
        const anySuccess = results.some(r => r.status === 'fulfilled' && r.value === true);
        if (anySuccess) {
            try {
                await deleteFetcher(`/seasons/${race.raceSeason.id}/results`);
            } catch (e) {
                showNotification("Nepodařilo se smazat sezónní výsledky", "error");
                console.error(e);
                return false;
            }
        }

        // Generate season results
        try {
            await postFetcher(`/seasons/${race.raceSeason.id}/results/generate`, {});
            showNotification("Sezónní výsledky byly úspěšně přepočítány.", "success");
        } catch (error: any) {
            if (error.response?.status === 404) {
                showNotification(" Není možné přepočítat sezónní výsledky. Pravděpodobně existuje jiný neukončený závod.", "error");
                return false;
            }
            showNotification("Nepodařilo se přepočítat sezónní výsledky", "error");
            console.error(error);
            return false;
        }
        return true;
    }

    const finishRace = async (): Promise<boolean> => {
        if (!race) return false;
        try {
            await putFetcher(`/races/${race.id}/status/finish`, {});
            showNotification("Závod označen jako ukončený", "success");
            return true;
        } catch (e) {
            showNotification("Nepodařilo se ukončit závod", "error");
            console.error(e);
        }
        return false;
    }

    if (isLoading || !race) return <Loading/>;
    if (error) return <ErrorMessage/>

    if (race.status !== "IN_PROGRESS") return <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="80vh"
    >
        <Typography variant="h4" fontWeight="bold">
            Závod není ve stavu zpracování
        </Typography>
    </Box>

    return (
        <>
            <Stack direction="column" spacing={2}>
                <Stack direction={{xs: "column", sm: "row"}} spacing={{xs: 0, sm: 5}} alignItems="center"
                       justifyContent="flex-start">
                    <Typography variant="h5" fontWeight="bold">
                        Organizace závodů
                    </Typography>
                    <Typography variant="h6">
                        {race.name} – {dayjs(race.date).format("DD.MM.YYYY")}
                    </Typography>
                </Stack>

                <TimeLineStepper steps={steps} handleStepChange={handleStepChange}/>
            </Stack>
            <PublishResultsDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onConfirm={handlePublishChoice}
            />
        </>
    );
}

export default RaceProcessing;
